import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ResourceType } from 'fleet-greenkeeper-bff-contracts';
import {
  type LayerSettingsByType,
  type RasterParams,
  type SlopePolygonsParams,
  CUTOFF_FIELDS,
} from '../models/RasterParams';
import { gktLayerConfig } from '~/config/app';
import { computeCutoffsFromRange } from '../utils/computeCutoffsFromRange';
import { computeElevationColorCutoffs, type ElevationRange } from '../utils/elevationContours';
import logger from '~/utils/logger/logger';

type LayerSettings = LayerSettingsByType;

type UpdateLayerSettingsPayload = {
  [K in ResourceType]: {
    layerType: K;
    settings: Partial<LayerSettings[K]>;
  };
}[ResourceType];

const defaultNdviSettings: RasterParams = {
  visible: true,
  opacity: 0.75,
  autoCutoff: true,
  cutoffMin: 0.13,
  mid: 0.21,
  cutoffMax: 0.29,
  colorVeryLow: [1, 0, 0], // #ff0000
  colorLow: [1, 1, 0], // #ffff00
  colorMedium: [100 / 255, 1, 100 / 255], // #64ff64
  colorHigh: [50 / 255, 127 / 255, 50 / 255], // #327f32
  dilationRadius: 0,
};

const defaultNdmiSettings: RasterParams = {
  visible: true,
  opacity: 0.75,
  autoCutoff: true,
  cutoffMin: 0,
  mid: 0.2,
  cutoffMax: 0.4,
  colorVeryLow: [1, 0, 0], // #ff0000
  colorLow: [1, 1, 0], // #ffff00
  colorMedium: [0, 127 / 255, 1], // #007fff
  colorHigh: [0, 0, 1], // #0000ff
  dilationRadius: 0,
};

const defaultNdreSettings: RasterParams = {
  visible: true,
  opacity: 0.75,
  autoCutoff: true,
  cutoffMin: 0.13,
  mid: 0.21,
  cutoffMax: 0.29,
  colorVeryLow: [225 / 255, 50 / 255, 30 / 255], // #e1321e - burnt red
  colorLow: [1, 165 / 255, 40 / 255], // #ffa528 - amber
  colorMedium: [50 / 255, 200 / 255, 180 / 255], // #32c8b4 - teal
  colorHigh: [15 / 255, 100 / 255, 90 / 255], // #0f645a - dark teal
  dilationRadius: 0,
};

const defaultCutIntensitySettings: RasterParams = {
  visible: true,
  opacity: 0.75,
  autoCutoff: false,
  cutoffMin: 0,
  mid: 0.5,
  cutoffMax: 1,
  colorVeryLow: [92 / 255, 51 / 255, 23 / 255], // #5c3317 - brown       (low cut intensity)
  colorLow: [204 / 255, 198 / 255, 130 / 255], // #ccc682 - tan
  colorMedium: [79 / 255, 137 / 255, 45 / 255], // #4f892d - medium green
  colorHigh: [0, 68 / 255, 0], // #004400 - darkest green (high cut intensity)
  dilationRadius: 0,
};

const defaultSlipHeatmapSettings: RasterParams = {
  visible: true,
  opacity: 0.75,
  autoCutoff: false,
  cutoffMin: 0,
  mid: 0.5,
  cutoffMax: 1,
  colorVeryLow: [1, 0.6980392156862745, 0.1803921568627451], // #ffb235 - light orange for very low slip density
  colorLow: [0.7019607843137254, 0.3843137254901961, 0.0196078431372549], // #b35f05 - medium color for medium slip density
  colorMedium: [1, 0, 0], // #ff0000 - lightest color for lowest slip density
  colorHigh: [0.30196078431372547, 0.043137254901960784, 0], // #4d0b00 - darkest color for highest slip density
  dilationRadius: 0,
};

const defaultSlopePolygonsSettings: SlopePolygonsParams = {
  visible: true,
  opacity: 0.75,
  showContours: true,
  autoCutoff: true,
  cutoffMin: 0.25,
  mid: 0.5,
  cutoffMax: 0.75,
  colorVeryLow: [34 / 255, 197 / 255, 94 / 255], // #22c55e
  colorLow: [250 / 255, 204 / 255, 21 / 255], // #facc15
  colorMedium: [249 / 255, 115 / 255, 22 / 255], // #f97316
  colorHigh: [220 / 255, 38 / 255, 38 / 255], // #dc2626
  dilationRadius: 0,
};

const initialState: LayerSettings = {
  ndvi: defaultNdviSettings,
  ndmi: defaultNdmiSettings,
  ndre: defaultNdreSettings,
  cutIntensity: defaultCutIntensitySettings,
  slipHeatmap: defaultSlipHeatmapSettings,
  slopePolygons: defaultSlopePolygonsSettings,
};

// Load from localStorage if available
const loadStateFromLocalStorage = (): LayerSettings => {
  try {
    const serializedState = localStorage.getItem(gktLayerConfig.layerSettingsKey);
    if (serializedState === null) {
      return initialState;
    }
    // Deep-merge with initialState so any new fields added to defaults
    // are preserved even when loading older saved state.
    const stored = JSON.parse(serializedState) as Partial<LayerSettings>;
    const { showContours: _storedNdviShowContours, ...storedNdvi } = (stored.ndvi ??
      {}) as Partial<RasterParams> & { showContours?: boolean };
    const { showContours: _storedNdmiShowContours, ...storedNdmi } = (stored.ndmi ??
      {}) as Partial<RasterParams> & { showContours?: boolean };
    const { showContours: _storedNdreShowContours, ...storedNdre } = (stored.ndre ??
      {}) as Partial<RasterParams> & { showContours?: boolean };
    const { showContours: _storedCutIntensityShowContours, ...storedCutIntensity } =
      (stored.cutIntensity ?? {}) as Partial<RasterParams> & { showContours?: boolean };
    const { showContours: _storedSlipHeatmapShowContours, ...storedSlipHeatmap } =
      (stored.slipHeatmap ?? {}) as Partial<RasterParams> & { showContours?: boolean };
    return {
      ndvi: { ...defaultNdviSettings, ...storedNdvi },
      ndmi: { ...defaultNdmiSettings, ...storedNdmi },
      ndre: { ...defaultNdreSettings, ...storedNdre },
      cutIntensity: { ...defaultCutIntensitySettings, ...storedCutIntensity },
      slipHeatmap: { ...defaultSlipHeatmapSettings, ...storedSlipHeatmap },
      slopePolygons: { ...defaultSlopePolygonsSettings, ...stored.slopePolygons },
    };
  } catch (err) {
    logger.error('Failed to load layer settings from localStorage:', err);
    return initialState;
  }
};

// Save to localStorage
const saveStateToLocalStorage = (state: LayerSettings) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(gktLayerConfig.layerSettingsKey, serializedState);
  } catch (err) {
    logger.error('Failed to save layer settings to localStorage:', err);
  }
};

const layerSettingsSlice = createSlice({
  name: 'greenkeeperLayerSettings',
  initialState: loadStateFromLocalStorage(),
  reducers: {
    updateLayerSettings: (state, action: PayloadAction<UpdateLayerSettingsPayload>) => {
      const { layerType, settings } = action.payload;
      // If the user manually adjusts a cutoff slider, disable autoCutoff
      const touchesCutoff = Object.keys(settings).some((key) =>
        CUTOFF_FIELDS.has(key as keyof RasterParams)
      );
      const currentSettings = state[layerType];
      if (touchesCutoff) {
        Object.assign(currentSettings, settings, { autoCutoff: false });
      } else {
        Object.assign(currentSettings, settings);
      }
      saveStateToLocalStorage(state);
    },
    resetLayerSettings: (state, action: PayloadAction<ResourceType>) => {
      const layerType = action.payload;
      switch (layerType) {
        case 'ndvi':
          state[layerType] = defaultNdviSettings;
          break;
        case 'ndmi':
          state[layerType] = defaultNdmiSettings;
          break;
        case 'ndre':
          state[layerType] = defaultNdreSettings;
          break;
        case 'cutIntensity':
          state[layerType] = defaultCutIntensitySettings;
          break;
        case 'slipHeatmap':
          state[layerType] = defaultSlipHeatmapSettings;
          break;
        case 'slopePolygons':
          state[layerType] = defaultSlopePolygonsSettings;
          break;
      }
      saveStateToLocalStorage(state);
    },
    resetAllLayerSettings: (state) => {
      state.ndvi = defaultNdviSettings;
      state.ndmi = defaultNdmiSettings;
      state.ndre = defaultNdreSettings;
      state.cutIntensity = defaultCutIntensitySettings;
      state.slipHeatmap = defaultSlipHeatmapSettings;
      state.slopePolygons = defaultSlopePolygonsSettings;
      saveStateToLocalStorage(state);
    },
    calibrateFromMetadata: (
      state,
      action: PayloadAction<{
        ndvi_min: number;
        ndvi_max: number;
        ndmi_min: number;
        ndmi_max: number;
        ndre_min: number;
        ndre_max: number;
      }>
    ) => {
      const { ndvi_min, ndvi_max, ndmi_min, ndmi_max, ndre_min, ndre_max } = action.payload;

      if (state.ndvi.autoCutoff) {
        const ndviCutoffs = computeCutoffsFromRange(ndvi_min, ndvi_max);
        state.ndvi = { ...state.ndvi, ...ndviCutoffs };
      }

      if (state.ndmi.autoCutoff) {
        const ndmiCutoffs = computeCutoffsFromRange(ndmi_min, ndmi_max);
        state.ndmi = { ...state.ndmi, ...ndmiCutoffs };
      }

      if (state.ndre.autoCutoff) {
        const ndreCutoffs = computeCutoffsFromRange(ndre_min, ndre_max);
        state.ndre = { ...state.ndre, ...ndreCutoffs };
      }

      saveStateToLocalStorage(state);
    },
    calibrateSlopeFromElevationRange: (
      state,
      action: PayloadAction<{
        elevationRange: ElevationRange;
        cutoffs?: { cutoffMin: number; mid: number; cutoffMax: number };
      }>
    ) => {
      if (state.slopePolygons.autoCutoff) {
        state.slopePolygons = {
          ...state.slopePolygons,
          ...(action.payload.cutoffs ??
            computeElevationColorCutoffs(action.payload.elevationRange)),
        };
      }

      saveStateToLocalStorage(state);
    },
  },
});

export const {
  updateLayerSettings,
  resetLayerSettings,
  resetAllLayerSettings,
  calibrateFromMetadata,
  calibrateSlopeFromElevationRange,
} = layerSettingsSlice.actions;

export default layerSettingsSlice.reducer;
